// deno-lint-ignore-file
import { useState, useEffect, useLayoutEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import API from "../../services/api.js"
import { useEvidence } from "../../hooks/useEvidence.js"
import EvidenceItem from "../../components/EvidenceItem.jsx"
import { motion } from 'framer-motion'
import { ArrowLeft, Plus, AlertTriangle, ShieldCheck, ShieldAlert, ChevronDown } from 'lucide-react'
import RedteamResolveModal from "../../components/RedteamResolveModal.jsx"


// ============================================================
// Badges
// ============================================================
function CoverageStatusBadge({ status }) {
  const styles = {
    mapped: 'bg-green-500/10 text-green-400 border border-green-500/20',
    partial: 'bg-yellow-500/10 text-yellow-400 border border-yellow-500/20',
    gap: 'bg-red-500/10 text-red-400 border border-red-500/20',
    not_applicable: 'bg-gray-500/10 text-gray-400 border border-gray-500/20'
  }
  return (
    <span
      className={`
        px-2 py-0.5 sm:px-3 sm:py-1
        rounded-full text-[10px] sm:text-xs font-medium
        inline-flex items-center gap-1.5 whitespace-nowrap
        ${styles[status] || styles.not_applicable}
      `}
    >
      {status?.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase())}
    </span>
  )
}

function ConfidenceBar({ score }) {
  const color = score >= 80 ? 'bg-green-500' : score >= 60 ? 'bg-yellow-500' : 'bg-red-500'
  return (
    <div className="flex items-center gap-2 shrink-0">
      <div className="w-16 sm:w-24 h-1.5 bg-white/10 rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${score}%` }} />
      </div>
      <span className="text-xs text-gray-400 tabular-nums">{score}%</span>
    </div>
  )
}

function SeverityBadge({ severity }) {
  const styles = {
    high: 'bg-red-500/10 text-red-400 border-red-500/20',
    medium: 'bg-orange-500/10 text-orange-400 border-orange-500/20',
    low: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20',
  }
  const key = severity?.toLowerCase()
  return (
    <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium border ${styles[key] || styles.low}`}>
      {severity}
    </span>
  )
}


function RequiresAttentionBanner({ control, unresolvedFindings, canResolve, onResolve }) {
  if (!unresolvedFindings || unresolvedFindings.length === 0) return null

  const isDangerousGap = true
  const severityRank = { high: 3, medium: 2, low: 1 }
  const worstSeverity = unresolvedFindings.reduce((worst, f) => {
    const key = f.severity?.toLowerCase()
    if (!worst) return key
    return (severityRank[key] || 0) > (severityRank[worst] || 0) ? key : worst
  }, null)

  return (
    <motion.div
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      className={`
        flex items-start justify-between gap-3
        px-4 sm:px-5 py-3 sm:py-4 rounded-xl sm:rounded-2xl
        ${isDangerousGap
          ? 'bg-red-500/10 border border-red-500/20'
          : 'bg-orange-500/10 border border-orange-500/20'}
      `}
    >
      <div className="flex items-start gap-3 min-w-0 flex-1">
        <AlertTriangle
          size={18}
          className={`shrink-0 mt-0.5 ${isDangerousGap ? 'text-red-400' : 'text-orange-400'}`}
        />
        <div>
          <p className={`text-sm font-medium ${isDangerousGap ? 'text-red-300' : 'text-orange-300'}`}>
            {isDangerousGap ? 'Requires attention' : 'Red-team alert'}
          </p>
          <p className={`text-xs mt-0.5 leading-relaxed ${isDangerousGap ? 'text-red-300/80' : 'text-orange-300/80'}`}>
            {isDangerousGap
              ? `Policy marks this control as ${control?.coverage_status}, but red-team testing found ${unresolvedFindings.length} failing detector${unresolvedFindings.length === 1 ? '' : 's'}. Review findings before relying on this control.`
              : 'Red-team testing found failing detectors for this control. Review the findings below.'
            }
          </p>
          {worstSeverity && (
            <p className="text-[11px] text-gray-500 mt-1">
              Severity: <span className="capitalize text-gray-400">{worstSeverity}</span>
            </p>
          )}
        </div>
      </div>

      {canResolve && (
        <button
          type="button"
          onClick={onResolve}
          className="
            shrink-0 flex items-center gap-1.5
            px-3 py-1.5 text-xs font-medium rounded-lg
            bg-white/5 border border-white/10 text-gray-300
            hover:text-white hover:bg-white/10 hover:border-gray-500/50
            transition-all duration-200 whitespace-nowrap
          "
        >
          <ShieldCheck size={13} />
          Mark Resolved
        </button>
      )}
    </motion.div>
  )
}

function RedteamEvidenceCard({ finding, index, canResolve, onResolve }) {
  const [expanded, setExpanded] = useState(false)
  const [resolving, setResolving] = useState(false)
  const [reason, setReason] = useState('')
  const [reasonError, setReasonError] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState(null)

  const isCompliant = finding.status === 'compliant'
  const isResolved = finding.status === 'resolved'

  function openResolveForm(e) {
    e.stopPropagation()
    setExpanded(false)
    setResolving(true)
  }

  function cancelResolve(e) {
    e?.stopPropagation()
    setResolving(false)
    setReason('')
    setSubmitError(null)
  }

  async function submitResolve(e) {
    e.stopPropagation()
    if (!reason.trim()) {
      setReasonError(true)
      setTimeout(() => setReasonError(false), 400)
      setSubmitError('Please provide a reason for resolving this finding.')
      return
    }
    setSubmitting(true)
    setSubmitError(null)
    try {
      await onResolve(finding.id, reason.trim())
      setResolving(false)
      setReason('')
    } catch (err) {
      setSubmitError(err.message)
    }
    setSubmitting(false)
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25, delay: index * 0.04 }}
      className={`
        bg-white/5 backdrop-blur-2xl
        border rounded-xl sm:rounded-2xl
        overflow-hidden
        ${isCompliant || isResolved ? 'border-green-500/20' : 'border-red-500/20'}
      `}
    >
      {/* FIX: this was a <button> containing the "Resolve" <button> below
          it — invalid HTML (button-in-button), which React flags as a
          hydration error and browsers handle inconsistently for
          click/keyboard focus. Changed to a div with role="button" +
          tabIndex + onKeyDown so it's still keyboard-accessible (Enter
          / Space toggles expand, same as a real button would), while
          the nested Resolve control stays a real, independently
          clickable <button>. */}
      <div
        role="button"
        tabIndex={0}
        onClick={() => !resolving && setExpanded(prev => !prev)}
        onKeyDown={(e) => {
          if (!resolving && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault()
            setExpanded(prev => !prev)
          }
        }}
        className="w-full flex items-start justify-between gap-3 p-4 sm:p-5 text-left hover:bg-white/[0.03] transition-colors cursor-pointer"
      >
        <div className="flex items-start gap-3 min-w-0 flex-1">
          {isCompliant || isResolved
            ? <ShieldCheck size={18} className="text-green-400 shrink-0 mt-0.5" />
            : <ShieldAlert size={18} className="text-red-400 shrink-0 mt-0.5" />
          }
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-sm font-medium text-white">{finding.detector_name}</p>
              <SeverityBadge severity={finding.severity} />
              {isResolved && (
                <span className="px-2 py-0.5 rounded-full text-[10px] font-medium border bg-green-500/10 text-green-400 border-green-500/20">
                  Resolved
                </span>
              )}
            </div>
            <p className="text-[11px] text-gray-500 mt-1">
              {finding.framework_name && `${finding.framework_name} · `}
              {finding.category}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <ConfidenceBar score={finding.confidence_score} />
          {canResolve && !isCompliant && !isResolved && (
            <button
              type="button"
              onClick={openResolveForm}
              className="
                flex items-center gap-1 px-2.5 py-1 text-[11px] font-medium rounded-md
                bg-white/5 border border-white/10 text-gray-300
                hover:text-white hover:bg-white/10 hover:border-gray-500/50
                transition-all duration-200 whitespace-nowrap
              "
            >
              <ShieldCheck size={11} />
              Resolve
            </button>
          )}
          <ChevronDown
            size={16}
            className={`text-gray-500 transition-transform ${expanded ? 'rotate-180' : ''}`}
          />
        </div>
      </div>

      {resolving && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          className="px-4 sm:px-5 pb-4 sm:pb-5 space-y-2.5"
          onClick={e => e.stopPropagation()}
        >
          <label className="block text-[11px] font-medium text-gray-400">
            Reason for resolving <span className="text-red-400">*</span>
          </label>
          <motion.div animate={reasonError ? { x: [-8, 8, -5, 5, 0] } : {}} transition={{ duration: 0.35 }}>
            <textarea
              value={reason}
              onChange={e => { setReason(e.target.value); if (submitError) setSubmitError(null) }}
              placeholder="e.g. Output filtering deployed for this detector category"
              rows={2}
              maxLength={500}
              className={`
                w-full px-3 py-2 rounded-lg text-xs
                bg-white/5 text-white placeholder-gray-500
                border transition-all duration-200 focus:outline-none resize-none
                ${reasonError
                  ? 'border-red-500 ring-1 ring-red-500'
                  : 'border-white/10 focus:border-green-500/50 focus:ring-1 focus:ring-green-500/30'
                }
              `}
            />
          </motion.div>

          {submitError && (
            <p className="text-xs text-red-400">{submitError}</p>
          )}

          <div className="flex items-center justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={cancelResolve}
              className="px-3 py-1.5 text-xs font-medium rounded-lg bg-white/5 border border-white/10 text-gray-300 hover:bg-white/10 transition-all duration-200"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={submitResolve}
              disabled={submitting}
              className="px-3 py-1.5 text-xs font-medium rounded-lg bg-green-600 hover:bg-green-500 text-white disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center gap-1.5"
            >
              <ShieldCheck size={12} />
              {submitting ? 'Resolving…' : 'Confirm Resolve'}
            </button>
          </div>
        </motion.div>
      )}

      {expanded && !resolving && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          className="px-4 sm:px-5 pb-4 sm:pb-5 space-y-3"
        >
          <div className="bg-white/5 border border-white/10 rounded-lg p-3">
            <p className="text-[10px] text-gray-500 mb-1 uppercase tracking-wider">Why this maps here</p>
            <p className="text-[11px] sm:text-xs text-gray-400 leading-relaxed">{finding.reasoning}</p>
          </div>

          {finding.prompt_text && (
            <div className="bg-white/5 border border-white/10 rounded-lg p-3">
              <p className="text-[10px] text-gray-500 mb-1 uppercase tracking-wider">Test Prompt</p>
              <p className="text-[11px] sm:text-xs text-gray-400 leading-relaxed font-mono break-words">
                {finding.prompt_text}
              </p>
            </div>
          )}

          {finding.response_text && (
            <div className="bg-white/5 border border-white/10 rounded-lg p-3">
              <p className="text-[10px] text-gray-500 mb-1 uppercase tracking-wider">Target Response</p>
              <p className="text-[11px] sm:text-xs text-gray-400 leading-relaxed font-mono break-words">
                {finding.response_text}
              </p>
            </div>
          )}

          {finding.evaluation_text && (
            <div className="bg-white/5 border border-white/10 rounded-lg p-3">
              <p className="text-[10px] text-gray-500 mb-1 uppercase tracking-wider">Evaluation</p>
              <p className="text-[11px] sm:text-xs text-gray-400 leading-relaxed">
                {finding.evaluation_text}
              </p>
            </div>
          )}

          {finding.prompt_id && (
            <p className="text-[10px] text-gray-600">Prompt ID: {finding.prompt_id}</p>
          )}
        </motion.div>
      )}
    </motion.div>
  )
}

function EvidenceUploadModal({ controlId: _controlId, orgId: _orgId, userId: _userId, onClose, onUpload }) {
  const [evidenceType, setEvidenceType] = useState('file')
  const [title, setTitle] = useState('')
  const [titleError, setTitleError] = useState(false)
  const [url, setUrl] = useState('')
  const [note, setNote] = useState('')
  const [expiryDate, setExpiryDate] = useState('')
  const [file, setFile] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)
  const fileInputRef = useRef(null)

  async function handleSubmit() {
    if (!title.trim()) {
      setError('Title is required')
      setTitleError(true)
      setTimeout(() => setTitleError(false), 400)
      return
    }
    if (evidenceType === 'file' && !file) { setError('Please select a file'); return }
    if (evidenceType === 'link' && !url.trim()) { setError('URL is required'); return }
    if (evidenceType === 'note' && !note.trim()) { setError('Note content is required'); return }

    setSubmitting(true)
    setError(null)
    try {
      await onUpload(evidenceType, { file, url, note, title, expiryDate })
      onClose()
    } catch (err) {
      setError(err.message)
    }
    setSubmitting(false)
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm px-0 sm:px-4"
    >
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25 }}
        className="
          w-full sm:max-w-lg
          rounded-t-2xl sm:rounded-2xl
          bg-gradient-to-b from-white/10 to-white/5
          backdrop-blur-2xl
          border border-white/10
          shadow-[0_-4px_40px_rgba(0,0,0,0.5)] sm:shadow-[0_10px_40px_rgba(0,0,0,0.6)]
          p-5 sm:p-6
          max-h-[92dvh] overflow-y-auto
        "
      >
        <div className="w-10 h-1 bg-white/20 rounded-full mx-auto mb-4 sm:hidden" />

        <h2 className="text-base sm:text-xl font-semibold text-white mb-5 sm:mb-6 tracking-tight">
          Add Evidence
        </h2>

        <div className="flex gap-2 mb-5 sm:mb-6">
          {[
            { key: 'file', label: '📄 File' },
            { key: 'link', label: '🔗 Link' },
            { key: 'note', label: '📝 Note' }
          ].map(({ key, label }) => (
            <button
              type="button"
              key={key}
              onClick={() => setEvidenceType(key)}
              className={`
                flex-1 sm:flex-none
                px-3 py-2 text-xs font-medium rounded-lg transition-all duration-200
                ${evidenceType === key
                  ? 'bg-blue-600 text-white border border-blue-500'
                  : 'bg-white/5 border border-white/10 text-gray-400 hover:bg-white/10 hover:text-white'
                }
              `}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="space-y-4">
          <motion.div
            animate={titleError ? { x: [-8, 8, -5, 5, 0] } : {}}
            transition={{ duration: 0.35 }}
          >
            <label className="block text-xs font-medium text-gray-400 mb-1.5">
              Title <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => {
                setTitle(e.target.value)
                if (titleError) { setTitleError(false); setError(null) }
              }}
              placeholder="e.g. Q3 Access Review Sign-off"
              className={`
                w-full px-4 py-3 rounded-lg text-sm
                bg-white/5 text-white placeholder-gray-500
                border transition-all duration-200 focus:outline-none
                ${titleError
                  ? 'border-red-500 ring-1 ring-red-500'
                  : 'border-white/10 focus:border-blue-500 focus:ring-1 focus:ring-blue-500'
                }
              `}
            />
          </motion.div>

          {evidenceType === 'file' && (
            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1.5">
                File <span className="text-red-400">*</span>
              </label>
              <input
                ref={fileInputRef}
                type="file"
                onChange={(e) => setFile(e.target.files[0])}
                className="
                  w-full px-4 py-3 rounded-lg text-sm
                  bg-white/5 border border-white/10 text-gray-300
                  file:mr-3 file:py-1 file:px-3 file:rounded-md file:border-0
                  file:text-xs file:bg-white/10 file:text-gray-300
                  hover:file:bg-white/20
                  focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500
                  transition-all duration-200
                "
              />
              {file && (
                <p className="text-[11px] text-gray-500 mt-1.5">
                  {file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)
                </p>
              )}
            </div>
          )}

          {evidenceType === 'link' && (
            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1.5">
                URL <span className="text-red-400">*</span>
              </label>
              <input
                type="url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://..."
                className="
                  w-full px-4 py-3 rounded-lg text-sm
                  bg-white/5 border border-white/10
                  text-white placeholder-gray-500
                  focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500
                  transition-all duration-200
                "
              />
            </div>
          )}

          {evidenceType === 'note' && (
            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1.5">
                Note <span className="text-red-400">*</span>
              </label>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Enter your note..."
                className="
                  w-full px-4 py-3 rounded-lg text-sm
                  bg-white/5 border border-white/10
                  text-white placeholder-gray-500 resize-none
                  focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500
                  transition-all duration-200
                "
                rows={3}
              />
            </div>
          )}

          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1.5">
              Expiry Date <span className="text-gray-600">(optional)</span>
            </label>
            <input
              type="date"
              value={expiryDate}
              onChange={(e) => setExpiryDate(e.target.value)}
              min={new Date().toISOString().split('T')[0]}
              className="
                w-full px-4 py-3 rounded-lg text-sm
                bg-white/5 border border-white/10 text-white
                focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500
                transition-all duration-200
              "
            />
          </div>
        </div>

        {error && (
          <motion.p
            initial={{ opacity: 0, y: -5 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-sm text-red-400 mt-4"
          >
            {error}
          </motion.p>
        )}

        <div className="flex flex-col-reverse sm:flex-row sm:justify-end items-stretch sm:items-center gap-2 sm:gap-3 mt-6 sm:mt-8">
          <button
            type="button"
            onClick={onClose}
            className="
              px-4 py-2.5 sm:py-2 text-sm font-medium rounded-lg
              bg-white/5 border border-white/10 text-gray-300
              hover:bg-white/10 hover:border-gray-500/50
              transition-all duration-200
            "
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting}
            className="
              px-4 py-2.5 sm:py-2 text-sm font-medium rounded-lg
              bg-blue-600 hover:bg-blue-500 text-white
              disabled:opacity-50 disabled:cursor-not-allowed
              transition-all duration-200
            "
          >
            {submitting ? 'Uploading…' : 'Add Evidence'}
          </button>
        </div>
      </motion.div>
    </motion.div>
  )
}

export default function ControlDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [control, setControl] = useState(null)
  const [mappings, setMappings] = useState([])
  const [redteamFindings, setRedteamFindings] = useState([])
  const [redteamLoading, setRedteamLoading] = useState(true)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [settingNA, setSettingNA] = useState(false)
  const [showEvidenceModal, setShowEvidenceModal] = useState(false)
  const [showResolveModal, setShowResolveModal] = useState(false)
  const [currentUser, setCurrentUser] = useState(null)
  const [assetId, setAssetId] = useState(null)
  const selectedApp = sessionStorage.getItem('selectedApp')
  useEffect(() => {
    let active = true
    setAssetId(null)
    if (!selectedApp) { setError('Select an application first.'); setLoading(false); return }
    API.get('/resolve-asset-id/', { params: { name: selectedApp } })
      .then(({ data }) => { if (active) setAssetId(data.id) })
      .catch(err => { if (active) { setError(err?.response?.data?.detail || err.message); setLoading(false) } })
    return () => { active = false }
  }, [selectedApp])

  const {
    evidence,
    loading: evidenceLoading,
    uploadFileEvidence,
    uploadLinkEvidence,
    uploadNoteEvidence,
    deleteEvidence,
    downloadEvidence
  } = useEvidence(id, assetId)

  useEffect(() => {
    if (!assetId) return
    fetchControl()
    fetchMappings()
    fetchCurrentUser()
    fetchRedteamFindings()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, assetId])

  useLayoutEffect(() => {
    // eslint-disable-next-line no-undef -- globalThis is a valid browser global (CRA's react-app eslint env doesn't declare it)
    globalThis.scrollTo(0, 0)
  }, [id])

  async function fetchCurrentUser() {
    try {
      const { data } = await API.get('/auth/me/')
      setCurrentUser({ ...data, id: data.user_id, org_id: data.tenant_id, full_name: data.email })
    } catch (err) { setError(err?.response?.data?.detail || err.message) }
  }

  async function fetchControl() {
    try {
      const { data } = await API.get(`/controls/${id}/`, { params: { asset_id: assetId } })
      setControl(data)
      setError(null)
    } catch (err) { setError(err?.response?.data?.detail || err.message) }
    finally { setLoading(false) }
  }

  async function fetchMappings() {
    try {
      const { data } = await API.get(`/controls/${id}/mappings/`, { params: { asset_id: assetId } })
      setMappings(data.sort((a, b) => new Date(b.questionnaire_answers?.answered_at || b.created_at || 0)
        - new Date(a.questionnaire_answers?.answered_at || a.created_at || 0)).slice(0, 1))
    } catch (err) { setError(err?.response?.data?.detail || err.message) }
  }

  async function fetchRedteamFindings() {
    setRedteamLoading(true)
    try {
      const { data } = await API.get(`/controls/${id}/findings/`, { params: { asset_id: assetId } })
      setRedteamFindings(data)
    } catch (err) { setError(err?.response?.data?.detail || err.message) }
    finally { setRedteamLoading(false) }
  }

  async function handleResolveFinding(findingId, reason) {
    await API.post(`/controls/${id}/resolve/`, { asset_id: assetId, finding_id: findingId, reason })
    await Promise.all([fetchRedteamFindings(), fetchControl()])
  }

  async function updateCoverage(status) {
    setSettingNA(true)
    try {
      const { data } = await API.patch(`/controls/${id}/coverage/`, { asset_id: assetId, status })
      setControl(prev => ({ ...prev, ...data }))
    } catch (err) { setError(err?.response?.data?.detail || err.message) }
    finally { setSettingNA(false) }
  }

  const handleSetNotApplicable = () => updateCoverage('not_applicable')
  const handleRevertToGap = () => updateCoverage('gap')

  async function handleEvidenceUpload(type, data) {
    const { title, expiryDate, file, url, note } = data
    if (type === 'file') await uploadFileEvidence(file, id, currentUser.org_id, currentUser.id, title, expiryDate)
    else if (type === 'link') await uploadLinkEvidence(url, id, currentUser.org_id, currentUser.id, title, expiryDate)
    else if (type === 'note') await uploadNoteEvidence(note, id, currentUser.org_id, currentUser.id, title, expiryDate)
  }

  const canManageEvidence = ['client_admin', 'super_admin'].includes(currentUser?.role)

  if (loading) {
    return (
      <div className="fixed inset-0 z-[9999] bg-black flex items-center justify-center overflow-hidden">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-white/10 border-t-blue-400 rounded-full animate-spin" />
          <p className="text-sm text-gray-400">Loading...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="fixed inset-0 z-[9999] bg-black flex items-center justify-center overflow-hidden">
        <div className="text-center">
          <p className="text-red-400 text-sm">{error}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="relative min-h-screen overflow-hidden">
      <div className="absolute inset-0 bg-cover bg-center" />
      <div className="absolute inset-0 bg-black/80" />

      {showEvidenceModal && (
        <EvidenceUploadModal
          controlId={id}
          orgId={currentUser?.org_id}
          userId={currentUser?.id}
          onClose={() => setShowEvidenceModal(false)}
          onUpload={handleEvidenceUpload}
        />
      )}

      {showResolveModal && (
        <RedteamResolveModal
          assetId={assetId}
          controlId={id}
          orgId={currentUser?.org_id}
          onClose={() => setShowResolveModal(false)}
          onResolved={() => {
            fetchControl()
            fetchRedteamFindings()
          }}
        />
      )}

      <div className="relative z-10">
         <div className="relative w-full max-w-[1400px] mx-auto px-3 md:px-4 lg:px-6 py-6 sm:py-8 min-w-0">

        <div className="mb-6 sm:mb-8 lg:mb-10">

          <div className="sm:hidden">
            <div className="flex items-center justify-between gap-2 mb-2">
              <div className="flex items-center gap-2">
                <motion.button
                  onClick={() => navigate('/grc/controls')}
                  whileTap={{ scale: 0.92 }}
                  className="text-gray-400 hover:text-white"
                >
                  <ArrowLeft className="w-5 h-5" />
                </motion.button>
                <p className="text-blue-400 text-base font-semibold">
                  {control?.control_id}
                </p>
              </div>
              <CoverageStatusBadge status={control?.coverage_status} />
            </div>

            <h1 className="text-xl font-semibold text-white leading-tight mb-1">
              {control?.title}
            </h1>

            <p className="text-xs text-gray-400 mb-0">
              {control?.frameworks?.name}
              {control?.frameworks?.version && ` · v${control.frameworks.version}`}
              {control?.category && ` · ${control.category}`}
            </p>

            {canManageEvidence && (
              <div className="pt-2">
                {control?.coverage_status === 'not_applicable' ? (
                  <button
                    type='button'
                    onClick={handleRevertToGap}
                    disabled={settingNA}
                    className="px-3 py-1.5 text-xs font-medium rounded-lg bg-white/5 border border-white/10 text-gray-300 hover:bg-white/10 transition-all duration-200 disabled:opacity-50"
                  >
                    Revert to Gap
                  </button>
                ) : (
                  <button
                    type='button'
                    onClick={handleSetNotApplicable}
                    disabled={settingNA}
                    className="px-3 py-1.5 text-xs font-medium rounded-lg bg-white/5 border border-white/10 text-gray-300 hover:bg-white/10 transition-all duration-200 disabled:opacity-50"
                  >
                    Mark N/A
                  </button>
                )}
              </div>
            )}
          </div>

          <div className="hidden sm:flex items-center justify-between gap-4">

            <div className="flex items-center gap-3 min-w-0 flex-1">

              <motion.button
                onClick={() => navigate('/grc/controls')}
                className="text-gray-400 hover:text-white"
              >
                <ArrowLeft className="w-5 h-5 mt-[-15px]" />
              </motion.button>

              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-blue-400 text-2xl font-semibold">
                    {control?.control_id}
                  </span>

                  <span className="text-white text-2xl font-semibold">
                    {control?.title}
                  </span>

                  <CoverageStatusBadge
                    status={control?.coverage_status}
                  />
                </div>

                <p className="text-xs text-gray-400 mt-1">
                  {control?.frameworks?.name}
                  {control?.frameworks?.version &&
                    ` · v${control.frameworks.version}`}
                  {control?.category &&
                    ` · ${control.category}`}
                </p>
              </div>

            </div>

            {canManageEvidence && (
              <div className="shrink-0">
                {control?.coverage_status === 'not_applicable' ? (
                  <button
                    type='button'
                    onClick={handleRevertToGap}
                    disabled={settingNA}
                    className="px-3 py-1.5 text-xs font-medium rounded-lg bg-white/5 border border-white/10 text-gray-300 hover:bg-white/10 transition-all duration-200 disabled:opacity-50"
                  >
                    Revert to Gap
                  </button>
                ) : (
                  <button
                    type='button'
                    onClick={handleSetNotApplicable}
                    disabled={settingNA}
                    className="px-3 py-1.5 text-xs font-medium rounded-lg bg-white/5 border border-white/10 text-gray-300 hover:bg-white/10 transition-all duration-200 disabled:opacity-50"
                  >
                    Mark N/A
                  </button>
                )}
              </div>
            )}

          </div>

        </div>

        <div className="space-y-4 sm:space-y-6">

          {!redteamLoading && (
            <RequiresAttentionBanner
              control={control}
              unresolvedFindings={redteamFindings.filter(f => f.status === 'non_compliant')}
              canResolve={canManageEvidence}
              onResolve={() => setShowResolveModal(true)}
            />
          )}

          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="bg-white/5 backdrop-blur-2xl border border-white/10 rounded-xl sm:rounded-2xl p-4 sm:p-6"
          >
            <h2 className="text-[10px] sm:text-xs font-semibold text-gray-400 mb-2.5 sm:mb-3 uppercase tracking-wider">
              Description
            </h2>
            <p className="text-xs sm:text-sm text-gray-300 leading-relaxed">{control?.description}</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.05 }}
          >
            <div className="flex items-center justify-between mb-3 sm:mb-4">
              <h2 className="text-sm sm:text-base lg:text-lg font-semibold text-white">
                Accepted Mappings
                <span className="text-gray-500 font-normal text-xs sm:text-sm ml-2">
                  ({mappings.length})
                </span>
              </h2>
            </div>

            {mappings.length === 0 ? (
              <div className="bg-white/5 backdrop-blur-2xl border border-white/10 rounded-xl sm:rounded-2xl p-5 sm:p-8 text-center">
                <p className="text-sm sm:text-base text-gray-300">No accepted mappings yet</p>
                <p className="text-xs text-gray-500 mt-1.5 sm:mt-2">
                  Accept mappings from the policy or questionnaire review pages to see them here
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {mappings.map((mapping, i) => (
                  <motion.div
                    key={`${mapping.source}-${mapping.id}`}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.25, delay: i * 0.04 }}
                    className="
                      bg-white/5 backdrop-blur-2xl
                      border border-green-500/20
                      rounded-xl sm:rounded-2xl
                      p-4 sm:p-5
                      hover:bg-white/[0.07]
                      transition-all duration-200
                    "
                  >
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <div className="min-w-0 flex-1">
                        {mapping.source === 'policy' ? (
                          <>
                            <p className="text-[11px] text-gray-500 mb-1 truncate">
                              From: {mapping.policy_clauses?.policies?.name}
                            </p>
                            {mapping.policy_clauses?.heading && (
                              <p className="text-xs sm:text-sm font-medium text-gray-300 leading-snug">
                                {mapping.policy_clauses.heading}
                              </p>
                            )}
                          </>
                        ) : (
                          <p className="text-[11px] text-gray-500 mb-1 truncate">
                            From questionnaire: {mapping.questionnaire_answers?.questionnaires?.title}
                          </p>
                        )}
                      </div>
                      {mapping.confidence_score != null && (
                        <ConfidenceBar score={mapping.confidence_score} />
                      )}
                    </div>

                    {mapping.source === 'policy' ? (
                      <p className="text-xs sm:text-sm text-gray-400 leading-relaxed mb-3">
                        {mapping.policy_clauses?.content}
                      </p>
                    ) : (
                      <div className="bg-white/5 border border-white/10 rounded-lg p-3 mb-3">
                        {mapping.questionnaire_answers?.questionnaire_questions?.question_text && (
                          <>
                            <p className="text-[10px] text-gray-500 mb-1 uppercase tracking-wider">Question</p>
                            <p className="text-xs sm:text-sm text-gray-300 leading-relaxed mb-2">
                              {mapping.questionnaire_answers.questionnaire_questions.question_text}
                            </p>
                          </>
                        )}
                        <p className="text-[10px] text-gray-500 mb-1 uppercase tracking-wider">Answer</p>
                        <p className="text-xs sm:text-sm text-gray-300 leading-relaxed">
                          {mapping.questionnaire_answers?.answer_value}
                        </p>
                      </div>
                    )}

                    <div className="bg-white/5 border border-white/10 rounded-lg p-3">
                      <p className="text-[10px] text-gray-500 mb-1 uppercase tracking-wider">Reasoning</p>
                      <p className="text-[11px] sm:text-xs text-gray-400 leading-relaxed">
                        {mapping.edited_reasoning || mapping.reasoning || 'No AI reasoning available for this mapping.'}
                      </p>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.08 }}
          >
            <div className="flex items-center justify-between mb-3 sm:mb-4">
              <h2 className="text-sm sm:text-base lg:text-lg font-semibold text-white flex items-center gap-2">
                Red-team Evidence
                <span className="text-gray-500 font-normal text-xs sm:text-sm">
                  ({redteamFindings.length})
                </span>
              </h2>
            </div>

            {redteamLoading ? (
              <div className="bg-white/5 backdrop-blur-2xl border border-white/10 rounded-xl sm:rounded-2xl p-6 text-center">
                <p className="text-sm text-gray-500">Loading red-team evidence…</p>
              </div>
            ) : redteamFindings.length === 0 ? (
              <div className="bg-white/5 backdrop-blur-2xl border border-dashed border-white/10 rounded-xl sm:rounded-2xl p-6 sm:p-8 text-center">
                <p className="text-sm sm:text-base text-gray-300">No red-team findings for this control yet</p>
                <p className="text-xs text-gray-500 mt-1.5 sm:mt-2">
                  Upload a red-team report from the Controls page to populate this section
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {redteamFindings.map((finding, i) => (
                  <RedteamEvidenceCard
                    key={finding.id}
                    finding={finding}
                    index={i}
                    canResolve={canManageEvidence}
                    onResolve={handleResolveFinding}
                  />
                ))}
              </div>
            )}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.1 }}
          >
            <div className="flex items-center justify-between mb-3 sm:mb-4">
              <h2 className="text-sm sm:text-base lg:text-lg font-semibold text-white">
                Evidence
                <span className="text-gray-500 font-normal text-xs sm:text-sm ml-2">
                  ({evidence.length})
                </span>
              </h2>

              {canManageEvidence && (
                <motion.button
                  onClick={() => setShowEvidenceModal(true)}
                  whileHover={{ scale: 1.04 }}
                  whileTap={{ scale: 0.97 }}
                  transition={{ type: 'spring', stiffness: 120, damping: 14, mass: 0.8 }}
                  className="
                    flex items-center gap-1.5 sm:gap-2
                    px-3 sm:px-4 py-1.5 sm:py-2
                    text-xs sm:text-sm font-medium rounded-xl
                    bg-white/5 border border-white/10 text-gray-200
                    hover:bg-white/10 focus:outline-none
                    transition-colors duration-200 whitespace-nowrap
                  "
                >
                  <Plus size={14} className="sm:hidden" />
                  <Plus size={16} className="hidden sm:block" />
                  <span className="hidden sm:inline">Add Evidence</span>
                  <span className="sm:hidden">Add</span>
                </motion.button>
              )}
            </div>

            {evidenceLoading ? (
              <div className="bg-white/5 backdrop-blur-2xl border border-white/10 rounded-xl sm:rounded-2xl p-6 text-center">
                <p className="text-sm text-gray-500">Loading evidence…</p>
              </div>
            ) : evidence.length === 0 ? (
              <div className="bg-white/5 backdrop-blur-2xl border border-dashed border-white/10 rounded-xl sm:rounded-2xl p-6 sm:p-8 text-center">
                <p className="text-sm sm:text-base text-gray-300">No evidence attached yet</p>
                {canManageEvidence && (
                  <p className="text-xs text-gray-500 mt-1.5 sm:mt-2">
                    Tap <span className="text-gray-300">+ Add Evidence</span> to attach files, links, or notes
                  </p>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                {evidence.map((item, i) => (
                  <motion.div
                    key={item.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.25, delay: i * 0.04 }}
                    className="
                      bg-white/5 backdrop-blur-2xl
                      border border-white/10
                      rounded-xl sm:rounded-2xl
                      px-4 sm:px-5 lg:px-6 py-3 sm:py-4
                      hover:bg-white/[0.07]
                      transition-all duration-200
                    "
                  >
                    <EvidenceItem
                      evidence={item}
                      onDelete={canManageEvidence ? deleteEvidence : null}
                      onDownload={downloadEvidence}
                    />
                  </motion.div>
                ))}
              </div>
            )}
          </motion.div>

        </div>
      </div>
      </div>
    </div>
  )
}